<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('travelOffer.createSubtitle') }} {{ $t('travelOffer.requiredFields') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--secondary" @click="$router.go(-1)">
          <v-icon small>mdi-arrow-left</v-icon>
          Retour
        </button>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== ASSISTANT ===== -->
      <div class="ov-card">
        <!-- Étapes -->
        <div class="co-steps">
          <div
            v-for="(label, idx) in stepLabels"
            :key="label"
            class="co-step-wrap"
            :class="{ 'co-step-wrap--last': idx === stepLabels.length - 1 }"
          >
            <div
              class="co-step"
              :class="{
                'co-step--active': e6 === idx + 1,
                'co-step--done': e6 > idx + 1,
                'co-step--clickable': e6 > idx + 1,
              }"
              @click="e6 > idx + 1 ? (e6 = idx + 1) : null"
            >
              <span class="co-step__num">
                <v-icon v-if="e6 > idx + 1" x-small color="white">mdi-check</v-icon>
                <template v-else>{{ idx + 1 }}</template>
              </span>
              <span class="co-step__label">{{ label }}</span>
            </div>
            <div v-if="idx < stepLabels.length - 1" class="co-step__line" :class="{ 'co-step__line--done': e6 > idx + 1 }"></div>
          </div>
        </div>

        <transition name="co-fade" mode="out-in">
          <!-- Étape 1 : Informations -->
          <div v-if="e6 === 1" key="1">
            <div class="ov-card__body">
              <div class="co-panel__title">{{ $t('travelOffer.generalInfo') }}</div>

              <div class="ov-row">
                <div class="ov-field">
                  <label class="ov-label">{{ $t('travelOffer.departureCity') }}<span class="ov-required">*</span></label>
                  <select
                    v-model="$v.offreVoyage.villeDepartDesignation.$model"
                    class="ov-select"
                    :class="{ 'is-invalid': $v.offreVoyage.villeDepartDesignation.$error }"
                  >
                    <option :value="null" disabled>Sélectionner</option>
                    <option v-for="ville in villesList" :key="ville.designation" :value="ville.designation">
                      {{ ville.designation }}
                    </option>
                  </select>
                  <div v-if="$v.offreVoyage.villeDepartDesignation.$error" class="ov-error">
                    {{ $t('travelOffer.requiredField') }}
                  </div>
                </div>

                <div class="ov-field">
                  <label class="ov-label">{{ $t('travelOffer.destinationCity') }}<span class="ov-required">*</span></label>
                  <select
                    v-model="$v.offreVoyage.villeDestinationDesignation.$model"
                    class="ov-select"
                    :class="{ 'is-invalid': $v.offreVoyage.villeDestinationDesignation.$error }"
                  >
                    <option :value="null" disabled>Sélectionner</option>
                    <option v-for="ville in destinationCities" :key="ville.designation" :value="ville.designation">
                      {{ ville.designation }}
                    </option>
                  </select>
                  <div v-if="$v.offreVoyage.villeDestinationDesignation.$error" class="ov-error">
                    {{
                      !$v.offreVoyage.villeDestinationDesignation.required
                        ? $t('travelOffer.requiredField')
                        : $t('travelOffer.destinationMustDiffer')
                    }}
                  </div>
                </div>

                <div class="ov-field ov-col-full">
                  <label class="ov-label">{{ $t('travelOffer.offerType') }}<span class="ov-required">*</span></label>
                  <select
                    v-model="$v.offreVoyage.typeOffreVoyageDesignation.$model"
                    class="ov-select"
                    :class="{ 'is-invalid': $v.offreVoyage.typeOffreVoyageDesignation.$error }"
                  >
                    <option :value="null" disabled>Sélectionner</option>
                    <option v-for="type in referenceTypeOffreVoyageList" :key="type.designation" :value="type.designation">
                      {{ type.designation }}
                    </option>
                  </select>
                  <div v-if="$v.offreVoyage.typeOffreVoyageDesignation.$error" class="ov-error">
                    {{ $t('travelOffer.requiredField') }}
                  </div>
                </div>

                <div class="ov-field ov-col-full">
                  <label class="ov-label">{{ $t('travelOffer.description') }}</label>
                  <textarea
                    v-model="$v.offreVoyage.description.$model"
                    class="ov-textarea"
                    :class="{ 'is-invalid': $v.offreVoyage.description.$error }"
                    rows="3"
                    placeholder="Décrivez brièvement l'offre..."
                  ></textarea>
                  <div v-if="$v.offreVoyage.description.$error" class="ov-error">
                    {{ $t('travelOffer.invalidField') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="ov-card__foot">
              <button type="button" class="ov-btn ov-btn--primary" @click="checkInfosOffreVoyage">
                {{ $t('travelOffer.continue') }}
                <v-icon small>mdi-arrow-right</v-icon>
              </button>
            </div>
          </div>

          <!-- Étape 2 : Prix -->
          <div v-else-if="e6 === 2" key="2">
            <div class="ov-card__body">
              <div class="co-panel__title">Définir les prix de voyage</div>

              <div class="ov-row ov-row--price">
                <div class="ov-field">
                  <label class="ov-label">Catégorie<span class="ov-required">*</span></label>
                  <select
                    v-model="$v.prixOffreVoyageModel.categorieVoyageurDesignation.$model"
                    class="ov-select"
                    :class="{ 'is-invalid': $v.prixOffreVoyageModel.categorieVoyageurDesignation.$error }"
                  >
                    <option :value="null" disabled>Sélectionner</option>
                    <option v-for="cat in referenceModeCategorieVoyageurList" :key="cat.designation" :value="cat.designation">
                      {{ cat.designation }}
                    </option>
                  </select>
                </div>

                <div class="ov-field">
                  <label class="ov-label">Mode<span class="ov-required">*</span></label>
                  <select
                    v-model="$v.prixOffreVoyageModel.modeDesignation.$model"
                    class="ov-select"
                    :class="{ 'is-invalid': $v.prixOffreVoyageModel.modeDesignation.$error }"
                  >
                    <option :value="null" disabled>Sélectionner</option>
                    <option v-for="mode in referenceModeOffreVoyageList" :key="mode.designation" :value="mode.designation">
                      {{ mode.designation }}
                    </option>
                  </select>
                </div>

                <div class="ov-field">
                  <label class="ov-label">Prix<span class="ov-required">*</span></label>
                  <div class="ov-input-suffix">
                    <input
                      v-model.number="$v.prixOffreVoyageModel.prix.$model"
                      type="number"
                      min="0"
                      class="ov-input"
                      :class="{ 'is-invalid': $v.prixOffreVoyageModel.prix.$error }"
                      placeholder="0"
                    />
                    <span>FCFA</span>
                  </div>
                </div>

                <button type="button" class="ov-btn ov-btn--navy" @click="savePriceInList">
                  <v-icon small>mdi-plus</v-icon>
                  Ajouter
                </button>
              </div>

              <div class="co-prices">
                <div v-if="priceList.length === 0" class="ov-empty">Aucun prix défini pour le moment.</div>

                <template v-else>
                  <div class="co-price-grid">
                    <div v-for="(price, p) in priceList" :key="p" class="co-price">
                      <div class="co-price__top">
                        <div>
                          <div class="co-price__cat">{{ price.categorieVoyageurDesignation || '—' }}</div>
                          <div class="co-price__mode">{{ price.modeDesignation || '—' }}</div>
                        </div>
                        <button type="button" class="ov-icon-btn ov-icon-btn--danger" @click="deleteItemInPriceList(p)">
                          <v-icon small>mdi-close</v-icon>
                        </button>
                      </div>
                      <div class="co-price__amount">{{ price.prix }} <span>FCFA</span></div>
                    </div>
                  </div>
                  <div class="co-price-summary">
                    <span>{{ priceList.length }} tarif(s)</span>
                    <span>Min : <strong>{{ minPrice === null ? '—' : minPrice + ' FCFA' }}</strong></span>
                    <span>Max : <strong>{{ maxPrice === null ? '—' : maxPrice + ' FCFA' }}</strong></span>
                  </div>
                </template>
              </div>
            </div>

            <div class="ov-card__foot">
              <button type="button" class="ov-btn ov-btn--secondary" @click="e6 = 1">
                <v-icon small>mdi-arrow-left</v-icon>
                {{ $t('travelOffer.previous') }}
              </button>
              <button type="button" class="ov-btn ov-btn--primary" @click="checkPricesBeforeValidation">
                {{ $t('travelOffer.continue') }}
                <v-icon small>mdi-arrow-right</v-icon>
              </button>
            </div>
          </div>

          <!-- Étape 3 : Validation -->
          <div v-else-if="e6 === 3" key="3">
            <div class="ov-card__body">
              <div class="co-panel__title">Vérifiez avant de créer l'offre</div>

              <div class="co-recap">
                <div class="ov-route co-recap__route">
                  <div class="ov-route__city">
                    <div class="ov-route__label">Départ</div>
                    <div class="ov-route__value">{{ offreVoyage.villeDepartDesignation || '—' }}</div>
                  </div>
                  <div class="ov-route__line"></div>
                  <div class="ov-route__city">
                    <div class="ov-route__label">Destination</div>
                    <div class="ov-route__value">{{ offreVoyage.villeDestinationDesignation || '—' }}</div>
                  </div>
                </div>

                <div class="ov-stat">
                  <span class="ov-stat__label">Type d'offre</span>
                  <span class="ov-stat__value">{{ offreVoyage.typeOffreVoyageDesignation || '—' }}</span>
                </div>
                <div class="ov-stat">
                  <span class="ov-stat__label">Description</span>
                  <span class="ov-stat__value">{{ offreVoyage.description || '—' }}</span>
                </div>
                <div class="ov-stat">
                  <span class="ov-stat__label">Prix définis</span>
                  <span class="ov-stat__value ov-stat__value--accent">{{ priceList.length }}</span>
                </div>
              </div>

              <div v-if="priceList.length" class="co-recap-prices">
                <div v-for="(price, i) in priceList" :key="i" class="co-recap-prices__row">
                  <span>{{ price.categorieVoyageurDesignation || '—' }}</span>
                  <span class="co-recap-prices__mode">{{ price.modeDesignation || '—' }}</span>
                  <strong>{{ price.prix }} FCFA</strong>
                </div>
              </div>

              <div v-else class="ov-note ov-note--warning mt-4">
                Aucun prix n'est défini. Vous pouvez terminer quand même, mais l'offre risque d'être incomplète.
              </div>
            </div>

            <div class="ov-card__foot">
              <button type="button" class="ov-btn ov-btn--secondary" @click="e6 = 2">
                <v-icon small>mdi-arrow-left</v-icon>
                {{ $t('travelOffer.previous') }}
              </button>
              <button type="button" class="ov-btn ov-btn--primary" @click="creerOffreVoyage">
                <v-icon small>mdi-check</v-icon>
                Créer l'offre
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- ===== RÉSUMÉ ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">{{ $t('travelOffer.summary') }}</div>
              <div class="ov-card__title">{{ $t('travelOffer.step') }} {{ e6 }} / 3</div>
            </div>
          </div>
          <div class="ov-card__body">
            <div class="ov-progress mb-4"><span :style="{ width: (e6 / 3) * 100 + '%' }"></span></div>

            <div class="ov-route mb-2">
              <div class="ov-route__city">
                <div class="ov-route__label">Départ</div>
                <div class="ov-route__value">{{ offreVoyage.villeDepartDesignation || '—' }}</div>
              </div>
              <div class="ov-route__line"></div>
              <div class="ov-route__city">
                <div class="ov-route__label">Arrivée</div>
                <div class="ov-route__value">{{ offreVoyage.villeDestinationDesignation || '—' }}</div>
              </div>
            </div>

            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('travelOffer.offerType') }}</span>
              <span class="ov-stat__value">{{ offreVoyage.typeOffreVoyageDesignation || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('travelOffer.pricesDefined') }}</span>
              <span class="ov-stat__value ov-stat__value--accent">{{ priceList.length }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Prix min.</span>
              <span class="ov-stat__value">{{ minPrice === null ? '—' : minPrice + ' FCFA' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Prix max.</span>
              <span class="ov-stat__value">{{ maxPrice === null ? '—' : maxPrice + ' FCFA' }}</span>
            </div>

            <div class="ov-note mt-4">
              La création n'est confirmée qu'à l'étape 3. Vous pouvez revenir en arrière sans perdre vos saisies.
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-warning" type="warning" width="350px" dismissible>{{ warningMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE,
  API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
  API_CREER_OFFRE_VOYAGE,
  HEADERS,
  API_GET_ALL_CATEGORY,
} from "@/components/globalConfig/globalConstConfig";
import { required, } from "vuelidate/lib/validators";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "../messages/messageProcess";

export default {
  name: "CreerOffreVoyage",
  data() {
    return {
      // ------------------------------ VARIABLES COMMUNES ----------------------------------- //
      e6: 1,
      successMsg: null,
      errorMsg: null,
      warningMsg: null,
      overlay: false,

      objectValue: {},

      objectToSend: {
        datas: [],
      },

      // ---------------------- VARIABLES EN RAPPORT AVEC LES VILLES ------------------------//

      search: "",
      headers: [
        { text: "ORDRE", value: "position" },
        { text: "VILLE", value: "villeDesignation" },
        { text: "ACTIONS", value: "actions", sortable: false },
      ],

      villeEscaleData: {
        offreVoyageDesignation: null,
        villeDesignation: null,
        position: null,
      },

      villesEscaleList: [],

      villesList: [],

      dialogForEscale: false,

      // ---------------------- VARIABLES EN RAPPORT AVEC LES OFFRES DE VOYAGE ------------------------//

      offreVoyageToSend: {
        datas: [],
      },

      offreVoyage: {
        designation: null,
        description: null,
        compagnieTransportRaisonSociale: null,
        typeOffreVoyageDesignation: null,
        villeDepartDesignation: null,
        villeDestinationDesignation: null,
        prixOffreVoyageDTOList: [],
        villeEscaleDTOList: [],
      },

      referenceTypeOffreVoyage: {
        referenceFamilleDesignation: "referenceFamilleTypeOffreVoyage",
      },

      referenceTypeOffreVoyageList: [],

      // ---------------------- VARIABLES EN RAPPORT AVEC LES PRIX DE VOYAGES ------------------------//

      dialogForPrice: false,

      prixOffreVoyageModel: {
        designation: null,
        prix: null,
        modeDesignation: null,
        categorieVoyageurDesignation: null,
        offreVoyageDesignation: null,
      },

      objectToSendReferenceModeOffre: {
        datas: [],
      },

      objectToSendReferenceModeCategorieVoyageur: {
        datas: [],
      },

      retrieveObjectCategory: {
        data: {
          compagnieTransportId:
            this.$store.state.userAuthentified.compagnieTransportId,
        },
      },

      referenceModeOffreVoyage: {
        referenceFamilleDesignation: "referenceFamilleModeVoyage",
      },

      referenceCategorieVoyageur: {
        referenceFamilleDesignation: "referenceFamilleCategorieVoyageur",
      },

      referenceModeOffreVoyageList: [],
      referenceModeCategorieVoyageurList: [],

      priceList: [],

      // ----------------------- FIN: VARIABLE EN RAPPORT AVEC LES PRIX DE VOYAGES --------------------------//
    };
  },

  validations: {
    // VALIDATION DES CONTRAINTES OFFRE DE VOYAGES

    offreVoyage: {
      description: {
        minLength(value) {
          if (value === null || value === undefined || value === "") return true;
          return String(value).length >= 4;
        },
      },
      villeDepartDesignation: {
        required,
      },
      villeDestinationDesignation: {
        required,
        differentFromDepart(value) {
          if (value === null || value === undefined || value === "") return true;
          return value !== this.offreVoyage.villeDepartDesignation;
        },
      },
      typeOffreVoyageDesignation: {
        required,
      },
    },

    // VALIDATION DES CONTRAINTES PRIX DE VOYAGES
    prixOffreVoyageModel: {

      prix: {
        required,
      },
      modeDesignation: {
        required,
      },

      categorieVoyageurDesignation: {
        required,
      },
    },

    // VALIDATION DES CONTRAINTES POUR LES VILLES ESCALES
    villeEscaleData: {
      /*offreVoyageDesignation: {
        required,
      },*/
      villeDesignation: {
        required,
      },
      position: {
        required,
      },
    },
  },

  methods: {
    // ENREGISTRER UNE VILLE ESCALE
    saveVilleEscale(villeEscale) {
      this.$v.$touch();
      if (
        this.$v.villeEscaleData.position.$invalid ||
        this.$v.villeEscaleData.villeDesignation.$invalid
      ) {
        this.errorMsg = "Le numero d'ordre et la ville escale sont obligatoire";
        showErrorMessage();
      } else {
        var escale = {
          position: null,
          villeDesignation: null,
          offreVoyageDesignation: null,
        };
        if (this.villesEscaleList.length == 0) {
          escale.position = villeEscale.position;
          escale.villeDesignation = villeEscale.villeDesignation;
          //escale.offreVoyageDesignation = villeEscale.offreVoyageDesignation;

          this.villesEscaleList.push(escale);
          villeEscale.position = null;
          villeEscale.villeDesignation = null;
        } else {
          this.villesEscaleList.forEach((element) => {
            if (element.villeDesignation == villeEscale.villeDesignation) {
              this.$swal.fire(
                "Erreur",
                "Vous avez déja enregistré cette ville",
                "error"
              );
            } else {
              escale.position = villeEscale.position;
              escale.villeDesignation = villeEscale.villeDesignation;
              //escale.offreVoyageDesignation = villeEscale.offreVoyageDesignation;

              this.villesEscaleList.push(escale);
              villeEscale.position = null;
              villeEscale.villeDesignation = null;
            }
          });
        }
      }
    },

    // SUPPRIMER DES VILLES ESCALES ENREGISTRÉS
    deleteItemInVilleEscaleList(item) {
      this.editedIndex = this.villesEscaleList.indexOf(item);
      this.villesEscaleList.splice(this.editedIndex, 1);
    },

    //ENREGISTRER UN NOUVEAU PRIX
    savePriceInList() {
      this.$v.$touch();
      if (this.$v.prixOffreVoyageModel.$invalid) {
        this.errorMsg = "Vous devez saisir tous les champs obligatoires";
        showErrorMessage();
      } else {
        var price = {
          designation: null,
          prix: null,
          modeDesignation: null,
          categorieVoyageurDesignation: null,
        };
        price.designation = this.prixOffreVoyageModel.categorieVoyageurDesignation;
        price.prix = this.prixOffreVoyageModel.prix;
        price.modeDesignation = this.prixOffreVoyageModel.modeDesignation;
        price.categorieVoyageurDesignation =
          this.prixOffreVoyageModel.categorieVoyageurDesignation;

        this.priceList.push(price);
        this.dialogForPrice = false;

        this.prixOffreVoyageModel.designation = null;
        this.prixOffreVoyageModel.modeDesignation = null;
        this.prixOffreVoyageModel.categorieVoyageurDesignation = null;
        this.prixOffreVoyageModel.prix = null;
      }
    },

    deleteItemInPriceList(position) {
      this.priceList.splice(position, 1);
    },

    //VERIFICATION DE L'ETAPE DE SAISIE DES INFORMATIONS D'OFFRE DE VOYAGE
    checkInfosOffreVoyage() {
      this.$v.$touch();
      if (this.$v.offreVoyage.$invalid) {
        this.errorMsg = "Vous n'avez saisi les champs concernés";
        showErrorMessage();
      } else this.e6 = 2;
    },

    checkPricesBeforeValidation() {
      this.e6 = 3;
    },
    //SOUMISSION DU FORMULAIRE
    submitForm() {
      this.$v.$touch();
      if (this.$v.offreVoyage.$invalid) {
        this.errorMsg = "Vous n'avez saisi les champs concernés";
        showErrorMessage();
      } else this.creerOffreVoyage();
    },

    //OBTENIR REFERENCE DESIGNATION TYPE OFFRE DE VOYAGE
    async obtenirReferenceModeOffreVoyage() {
      this.objectToSendReferenceModeOffre.datas.push(
        this.referenceModeOffreVoyage
      );
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferenceModeOffre,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          console.log("Mode de voyage", response);
          this.referenceModeOffreVoyageList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    //OBTENIR REFERENCE DESIGNATION CATEGORIE VOYAGEUR
    async getAllCategoryByCompagnieTransport() {
      await axios
        .post(API_GET_ALL_CATEGORY, this.retrieveObjectCategory, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          this.referenceModeCategorieVoyageurList =
            response.data.item.referenceDTOS;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    //OBTENIR REFERENCE DESIGNATION TYPE OFFRE DE VOYAGE
    async obtenirReferenceTypeOffreVoyage() {
      this.objectToSend.datas.push(this.referenceTypeOffreVoyage);
      await axios
        .post(API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE, this.objectToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          this.referenceTypeOffreVoyageList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // CREATION D'UNE OFFRE DE VOYAGE
    async creerOffreVoyage() {
      this.offreVoyage.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;

      if (
        this.offreVoyage.designation === null ||
        this.offreVoyage.designation === undefined ||
        this.offreVoyage.designation === ""
      ) {
        const depart = this.offreVoyage.villeDepartDesignation || "";
        const dest = this.offreVoyage.villeDestinationDesignation || "";
        this.offreVoyage.designation = `${depart} → ${dest}`.trim();
      }

      this.offreVoyage.prixOffreVoyageDTOList = this.priceList;
      this.offreVoyage.villeEscaleDTOList = this.villesEscaleList;
      this.prixOffreVoyageDTOList = this.priceList;
      this.offreVoyageToSend.datas.push(this.offreVoyage);

      this.overlay = true;
      await axios
        .post(API_CREER_OFFRE_VOYAGE, this.offreVoyageToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.offreVoyageToSend.datas = [];
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
              this.offreVoyageToSend.datas = [];
            }
          } else if (response.status == 204) {
            this.warningMsg =
              "Erreur , lors de la création de l'offre de voyage";
            showWarningMessage();
            this.offreVoyageToSend.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.offreVoyageToSend.datas = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.offreVoyageToSend.datas = [];
        })
        .finally(() => {
          this.offreVoyage.designation = null;
          this.offreVoyage.description = null;
          this.offreVoyage.typeOffreVoyageDesignation = null;
          this.offreVoyage.villeDepartDesignation = null;
          this.offreVoyage.villeDestinationDesignation = null;
          this.priceList = [];
          this.villesEscaleList = [];
          this.e6 = 1;
          this.$v.$reset();
          this.overlay = false;
        });
    },

    // RECUPERER LA LISTE DES VILLES ENREGISTRÉES
    async getAllCities() {
      this.loading = false;
      await axios
        .post(API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE, this.objectValue, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          this.villesList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    //VERIFIE SI CEST UN NOMBRE POSITIF
    isNumber(item) {
      return item > 0;
    },
  },

  computed: {
    stepLabels() {
      return [
        this.$t('travelOffer.informations'),
        this.$t('travelOffer.prices'),
        this.$t('travelOffer.validation'),
      ];
    },
    destinationCities() {
      if (!Array.isArray(this.villesList)) return [];
      const depart = this.offreVoyage && this.offreVoyage.villeDepartDesignation;
      if (!depart) return this.villesList;
      return this.villesList.filter((v) => v && v.designation !== depart);
    },

    minPrice() {
      if (!this.priceList || this.priceList.length === 0) return null;

      const values = this.priceList
        .map((p) => Number(p && p.prix))
        .filter((n) => Number.isFinite(n));

      if (values.length === 0) return null;
      return Math.min(...values);
    },

    maxPrice() {
      if (!this.priceList || this.priceList.length === 0) return null;

      const values = this.priceList
        .map((p) => Number(p && p.prix))
        .filter((n) => Number.isFinite(n));

      if (values.length === 0) return null;
      return Math.max(...values);
    },

    descriptionOffreVoyageErrors() {
      const errors = [];
      if (!this.$v.offreVoyage.description.$dirty) return errors;
      !this.$v.offreVoyage.description.minLength &&
        errors.push("Au moins quatres (4) caractères pour la description");
      return errors;
    },

    villeDepartErrors() {
      const errors = [];
      if (!this.$v.offreVoyage.villeDepartDesignation.$dirty) return errors;
      !this.$v.offreVoyage.villeDepartDesignation.required &&
        errors.push("La ville de depart est obligatoire.");
      return errors;
    },

    villeDestinationErrors() {
      const errors = [];
      if (!this.$v.offreVoyage.villeDestinationDesignation.$dirty)
        return errors;
      !this.$v.offreVoyage.villeDestinationDesignation.required &&
        errors.push("La ville de destination est obligatoire.");
      !this.$v.offreVoyage.villeDestinationDesignation.differentFromDepart &&
        errors.push("La destination doit être différente de la ville de départ.");
      return errors;
    },

    typeOffreVoyageErrors() {
      const errors = [];
      if (!this.$v.offreVoyage.typeOffreVoyageDesignation.$dirty) return errors;
      !this.$v.offreVoyage.typeOffreVoyageDesignation.required &&
        errors.push("Le type d'offre de voyage est obligatoire.");
      return errors;
    },

    // GESTION DES CONTRAINTES DE CHAMPS D'ENTREES POUR LES PRIX DE VOYAGE

    designationPrixOffreVoyageErrors() {
      const errors = [];
      if (!this.$v.prixOffreVoyageModel.designation.$dirty) return errors;
      !this.$v.prixOffreVoyageModel.designation.required &&
        errors.push("La désignation est obligatoire.");
      return errors;
    },

    pricePrixOffreVoyageErrors() {
      const errors = [];
      if (!this.$v.prixOffreVoyageModel.prix.$dirty) return errors;
      !this.isNumber(this.prixOffreVoyageModel.prix) &&
        errors.push("Saisissez un prix correct.");
      !this.$v.prixOffreVoyageModel.prix.required &&
        errors.push("Le prix est obligatoire.");
      return errors;
    },

    modePrixOffreVoyageErrors() {
      const errors = [];
      if (!this.$v.prixOffreVoyageModel.modeDesignation.$dirty) return errors;
      !this.$v.prixOffreVoyageModel.modeDesignation.required &&
        errors.push("La selection du mode de l'offre est obligatoire.");
      return errors;
    },

    categoriePrixOffreVoyageErrors() {
      const errors = [];
      if (!this.$v.prixOffreVoyageModel.categorieVoyageurDesignation.$dirty)
        return errors;
      !this.$v.prixOffreVoyageModel.categorieVoyageurDesignation.required &&
        errors.push("Veuillez selectionner une catégorie.");
      return errors;
    },

    // CONTRAINTES POUR L'ENREGISTREMENT DES VILLES ESCALES

    villeDesignation() {
      const errors = [];
      if (!this.$v.villeEscaleData.villeDesignation.$dirty) return errors;
      !this.$v.villeEscaleData.villeDesignation.required &&
        errors.push("Le champs ville est obligatoire.");
      return errors;
    },

    positionRequired() {
      const errors = [];
      if (!this.$v.villeEscaleData.position.$dirty) return errors;
      !this.$v.villeEscaleData.position.required &&
        errors.push("Definissez une position.");
      return errors;
    },
  },

  mounted() {
    this.getAllCities();
    this.obtenirReferenceTypeOffreVoyage();
    this.obtenirReferenceModeOffreVoyage();
    this.getAllCategoryByCompagnieTransport();
    //this.obtenirReferenceModeCategorieVoyageur();
  },

  watch: {
    "offreVoyage.villeDepartDesignation"(newValue) {
      if (
        newValue &&
        this.offreVoyage &&
        this.offreVoyage.villeDestinationDesignation === newValue
      ) {
        this.offreVoyage.villeDestinationDesignation = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

/* ÉTAPES */
.co-steps {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid $ov-line;
  background: $ov-soft;
}

.co-step-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  &--last {
    flex: 0 0 auto;
  }
}

.co-step {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $ov-subtle;

  &--clickable {
    cursor: pointer;
  }

  &--active,
  &--done {
    color: $ov-navy;
  }
}

.co-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 800;
  color: $ov-muted;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.14);
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  .co-step--active & {
    color: #ffffff;
    background: $ov-navy;
    border-color: $ov-navy;
    box-shadow: 0 0 0 4px rgba(12, 36, 97, 0.1);
  }

  .co-step--done & {
    background: $ov-green;
    border-color: $ov-green;
  }
}

.co-step__label {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.co-step__line {
  flex: 1;
  min-width: 16px;
  height: 2px;
  border-radius: 2px;
  background: rgba(15, 23, 42, 0.1);

  &--done {
    background: $ov-green;
  }
}

.co-panel__title {
  font-size: 16px;
  font-weight: 800;
  color: $ov-navy;
  margin-bottom: 18px;
}

.co-fade-enter-active,
.co-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.co-fade-enter,
.co-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* PRIX */
.co-prices {
  margin-top: 22px;
}

.co-price-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.co-price {
  padding: 14px;
  border: 1px solid $ov-line;
  border-left: 3px solid $ov-green;
  border-radius: 12px;
  background: #ffffff;
}

.co-price__top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.co-price__cat {
  font-size: 14px;
  font-weight: 800;
  color: $ov-navy;
}

.co-price__mode {
  font-size: 12px;
  color: $ov-muted;
}

.co-price__amount {
  margin-top: 10px;
  font-size: 20px;
  font-weight: 900;
  color: $ov-green;

  span {
    font-size: 12px;
    font-weight: 700;
    color: $ov-subtle;
  }
}

.co-price-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 14px;
  font-size: 13px;
  color: $ov-muted;

  strong {
    color: $ov-navy;
  }
}

/* RÉCAPITULATIF */
.co-recap__route {
  padding: 16px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: $ov-soft;
}

.co-recap-prices {
  margin-top: 16px;
  border: 1px solid $ov-line;
  border-radius: 12px;
  overflow: hidden;
}

.co-recap-prices__row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 12px;
  padding: 11px 16px;
  font-size: 13px;
  border-bottom: 1px solid $ov-line;

  &:last-child {
    border-bottom: none;
  }

  strong {
    color: $ov-navy;
  }
}

.co-recap-prices__mode {
  color: $ov-muted;
}

@media (max-width: 760px) {
  .co-steps {
    padding: 14px 16px;
  }

  .co-step__label {
    display: none;
  }

  .co-step--active .co-step__label {
    display: inline;
  }
}
</style>
